import { CpmButton } from '../../../components/design-system';
import { useAppDispatch } from '../../../store';
import {
    setFieldIncluded,
    setFieldValue,
    setManyIncluded,
} from '../../../store/slices/configTemplateDraftSlice';
import type {
    ConfigTemplateCluster,
    ConfigTemplateDraft,
    ConfigTemplateFieldSchema,
    ConfigTemplatePart,
} from '../../../types/configTemplate.types';
import {
    CONFIG_TEMPLATE_COPY,
    paneChangedCountLine,
    paneCountLine,
    paneEmptyBody,
    paneEmptyTitle,
} from '../configTemplateCopy';
import { clusterCardTitle } from '../utils/fieldMeta';
import { fieldMatchesQuery, isSettingChanged } from '../utils/settingsDiff';
import FieldControl from './FieldControl';

type CopyBaseline = ConfigTemplateDraft['copyBaseline'];

interface SettingsPaneProps {
    groupTitle?: string;
    part: ConfigTemplatePart | null;
    included: string[];
    values: Record<string, unknown>;
    copyBaseline?: CopyBaseline;
    view: 'all' | 'in';
    mode?: 'part' | 'changed';
    searchQuery?: string;
    copySourceName?: string | null;
    onShowEverySetting?: () => void;
}

function ClusterCard({
    cluster,
    partId,
    included,
    values,
    copyBaseline,
}: {
    cluster: ConfigTemplateCluster;
    partId?: string;
    included: string[];
    values: Record<string, unknown>;
    copyBaseline?: CopyBaseline;
}) {
    const dispatch = useAppDispatch();
    const includedSet = new Set(included);
    const fields = cluster.fields ?? [];
    const defaults: Record<string, unknown> = {};
    for (const field of fields) {
        if (field.path in values) defaults[field.path] = values[field.path];
        else if (field.valueKind === 'boolean') defaults[field.path] = false;
        else if (field.valueKind === 'number') defaults[field.path] = 0;
        else if (field.valueKind === 'string[]' || field.valueKind === 'object[]') {
            defaults[field.path] = [];
        } else defaults[field.path] = field.enumValues?.[0] ?? '';
    }

    const inCount = fields.filter((field) => includedSet.has(field.path)).length;
    const allIn = fields.length > 0 && inCount === fields.length;
    const clusterChanged = fields.some((field) =>
        isSettingChanged(field.path, includedSet.has(field.path), values[field.path], copyBaseline)
    );
    const cardTitle = clusterCardTitle(cluster.partId ?? partId, cluster.title);
    const nested = Boolean(cardTitle) || fields.length > 1;

    return (
        <div
            className="bg-white shrink-0 overflow-hidden"
            style={{
                border: '1px solid var(--cpm-border-default)',
                borderRadius: 'var(--cpm-radius-10)',
                minHeight: 'min-content',
            }}
        >
            {cardTitle ? (
                <div
                    className="flex flex-wrap items-center"
                    style={{
                        gap: 9,
                        padding: '10px 13px',
                        background: 'var(--cpm-purple-tint-3)',
                        borderBottom: '1px solid var(--cpm-border-default)',
                    }}
                >
                    <span
                        style={{
                            fontSize: 13,
                            fontWeight: 800,
                            letterSpacing: '-0.01em',
                            color: 'var(--cpm-text-primary)',
                        }}
                    >
                        {cardTitle}
                    </span>
                    <span
                        className="tabular-nums"
                        style={{
                            fontSize: 10.5,
                            fontWeight: 800,
                            color:
                                inCount > 0
                                    ? 'var(--cpm-brand-purple)'
                                    : 'var(--cpm-text-secondary)',
                            background:
                                inCount > 0
                                    ? 'var(--cpm-purple-tint-13)'
                                    : 'var(--cpm-surface-tag)',
                            borderRadius: 'var(--cpm-radius-full)',
                            padding: '2px 8px',
                        }}
                    >
                        {inCount}/{fields.length}
                    </span>
                    {clusterChanged ? (
                        <span
                            className="uppercase"
                            style={{
                                fontSize: 10.5,
                                fontWeight: 800,
                                letterSpacing: '0.06em',
                                color: 'var(--cpm-brand-purple)',
                            }}
                        >
                            {CONFIG_TEMPLATE_COPY.CHANGED_SINCE_COPY}
                        </span>
                    ) : null}
                    <div className="flex-1" />
                    {nested ? (
                        <CpmButton
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                                dispatch(
                                    setManyIncluded({
                                        paths: fields.map((field) => field.path),
                                        included: !allIn,
                                        defaults,
                                    })
                                )
                            }
                        >
                            {allIn
                                ? CONFIG_TEMPLATE_COPY.TAKE_ALL_OUT
                                : CONFIG_TEMPLATE_COPY.PUT_ALL_IN}
                        </CpmButton>
                    ) : null}
                </div>
            ) : null}
            <div className="flex flex-col">
                {fields.map((field: ConfigTemplateFieldSchema) => (
                    <FieldControl
                        key={field.path}
                        field={field}
                        clusterTitle={cluster.title}
                        included={includedSet.has(field.path)}
                        value={values[field.path]}
                        changed={isSettingChanged(
                            field.path,
                            includedSet.has(field.path),
                            values[field.path],
                            copyBaseline
                        )}
                        onIncludedChange={(next, fallback) =>
                            dispatch(
                                setFieldIncluded({
                                    path: field.path,
                                    included: next,
                                    value: fallback,
                                })
                            )
                        }
                        onValueChange={(next) =>
                            dispatch(setFieldValue({ path: field.path, value: next }))
                        }
                    />
                ))}
            </div>
        </div>
    );
}

export default function SettingsPane({
    groupTitle,
    part,
    included,
    values,
    copyBaseline,
    view,
    mode = 'part',
    searchQuery = '',
    copySourceName,
    onShowEverySetting,
}: SettingsPaneProps) {
    if (!part) {
        return (
            <div
                className="min-w-0 flex-1 bg-white"
                style={{
                    border: '1px solid var(--cpm-border-default)',
                    borderRadius: 'var(--cpm-radius-xl)',
                    padding: 18,
                    minHeight: 0,
                }}
            >
                <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--cpm-text-primary)' }}>
                    Select a settings group
                </div>
            </div>
        );
    }

    const includedSet = new Set(included);
    const query = searchQuery.trim().toLowerCase();
    const clusters = part.clusters
        .map((cluster) => ({
            ...cluster,
            fields: (cluster.fields ?? []).filter((field) => {
                if (view === 'in' && !includedSet.has(field.path)) return false;
                if (
                    query &&
                    !fieldMatchesQuery(field, query, {
                        partId: cluster.partId ?? part.id,
                        clusterTitle: cluster.title,
                    })
                ) {
                    return false;
                }
                return true;
            }),
        }))
        .filter((cluster) => (cluster.fields ?? []).length > 0);
    const allFields = part.clusters.flatMap((cluster) => cluster.fields ?? []);
    const visibleFields = clusters.flatMap((cluster) => cluster.fields ?? []);
    const inCount = visibleFields.filter((field) => includedSet.has(field.path)).length;
    const empty = clusters.length === 0;

    return (
        <div
            className="min-w-0 flex-1 flex flex-col bg-white"
            style={{
                border: '1px solid var(--cpm-border-default)',
                borderRadius: 'var(--cpm-radius-xl)',
                minHeight: 0,
                overflow: 'hidden',
            }}
        >
            <div
                className="flex flex-wrap items-baseline flex-none"
                style={{
                    gap: 10,
                    padding: '12px 16px',
                    borderBottom: '1px solid var(--cpm-border-default)',
                    background: 'var(--cpm-surface-subtle)',
                }}
            >
                {groupTitle ? (
                    <span
                        className="uppercase"
                        style={{
                            fontSize: 10.5,
                            fontWeight: 800,
                            letterSpacing: '0.08em',
                            color: 'var(--cpm-text-tertiary)',
                        }}
                    >
                        {groupTitle}
                    </span>
                ) : null}
                <span
                    style={{
                        fontSize: 15,
                        fontWeight: 800,
                        letterSpacing: '-0.015em',
                        color: 'var(--cpm-text-primary)',
                    }}
                >
                    {part.title}
                </span>
                <div className="flex-1" />
                <span
                    className="tabular-nums"
                    style={{ fontSize: 12, fontWeight: 700, color: 'var(--cpm-text-secondary)' }}
                >
                    {mode === 'changed'
                        ? paneChangedCountLine(visibleFields.length)
                        : paneCountLine(inCount, query ? visibleFields.length : allFields.length)}
                </span>
            </div>

            <div
                className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
                style={{ padding: '14px 16px 18px' }}
            >
                <div className="flex flex-col" style={{ gap: 12, minHeight: 'min-content' }}>
                {empty ? (
                    <div
                        className="flex flex-col"
                        style={{
                            gap: 6,
                            padding: 20,
                            border: '1px dashed var(--cpm-border-strong)',
                            borderRadius: 'var(--cpm-radius-10)',
                            background: 'var(--cpm-surface-subtle)',
                        }}
                    >
                        <div
                            style={{
                                fontSize: 13.5,
                                fontWeight: 800,
                                color: 'var(--cpm-text-primary)',
                            }}
                        >
                            {paneEmptyTitle({
                                query: query || undefined,
                                changed: mode === 'changed',
                                inOnly: view === 'in',
                            })}
                        </div>
                        <div
                            style={{
                                fontSize: 12.5,
                                fontWeight: 500,
                                lineHeight: 1.55,
                                color: 'var(--cpm-text-secondary)',
                                maxWidth: 560,
                            }}
                        >
                            {paneEmptyBody({
                                query: query || undefined,
                                changed: mode === 'changed',
                                sourceName: copySourceName,
                            })}
                        </div>
                        {view === 'in' && !query && mode !== 'changed' && onShowEverySetting ? (
                            <div className="flex" style={{ gap: 9, marginTop: 6 }}>
                                <CpmButton
                                    variant="outline"
                                    size="sm"
                                    onClick={onShowEverySetting}
                                >
                                    {CONFIG_TEMPLATE_COPY.SHOW_EVERY_SETTING}
                                </CpmButton>
                            </div>
                        ) : null}
                    </div>
                ) : (
                    clusters.map((cluster, index) => (
                        <ClusterCard
                            key={cluster.id ?? `cluster-${index}`}
                            cluster={cluster}
                            partId={part.id}
                            included={included}
                            values={values}
                            copyBaseline={copyBaseline}
                        />
                    ))
                )}
                <div
                    className="shrink-0"
                    style={{
                        fontSize: 11.5,
                        fontWeight: 600,
                        lineHeight: 1.5,
                        color: 'var(--cpm-text-secondary)',
                    }}
                >
                    {CONFIG_TEMPLATE_COPY.PANE_OMIT_NOTE}
                </div>
                </div>
            </div>
        </div>
    );
}
